import React, { useEffect } from 'react';
import { Box, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useSidebar } from '../../context/SidebarContext';
import UnifiedSidebar from './UnifiedSidebar';
import MobileHeader from './MobileHeader';

/**
 * AppShell - Main layout wrapper
 *
 * Structure:
 * - Desktop/Tablet: UnifiedSidebar (280px expanded / 48px collapsed) + Main content
 * - Phone: MobileHeader (top) + Main content
 */

// Routes where AppShell should NOT render navigation (auth pages, onboarding)
const NO_SIDEBAR_ROUTES = ['/login', '/register', '/registro-freemium'];
const ONBOARDING_PREFIX = '/onboarding/';

const AppShell = ({ children }) => {
  const location = useLocation();
  const theme = useTheme();
  const {
    isMobile,
    handleToggleShortcut,
    isOverlayOpen,
    closeOverlay,
    openSearch,
  } = useSidebar();

  // Keyboard shortcuts: Cmd/Ctrl+B toggle, Cmd/Ctrl+K search, Escape close overlay
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'b') {
        event.preventDefault();
        handleToggleShortcut();
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        openSearch();
        return;
      }

      if (event.key === 'Escape' && isOverlayOpen) {
        event.preventDefault();
        closeOverlay();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleToggleShortcut, closeOverlay, isOverlayOpen, openSearch]);

  // Check if we should show navigation
  const shouldShowNav =
    !NO_SIDEBAR_ROUTES.includes(location.pathname) &&
    !location.pathname.startsWith(ONBOARDING_PREFIX);

  // Main content margin: NOT needed — StyledDrawer root element
  // already acts as a flex spacer (width: 280px/48px).
  // Adding marginLeft would double-count and create a gray gap.
  const getMainContentStyles = () => {
    return {};
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100dvh',
        maxHeight: '100dvh',
        overflow: 'hidden',
        backgroundColor: 'background.default',
        // #1722: ProviderStatusBanner publica su altura aquí; sin él vale 0 y nada cambia.
        boxSizing: 'border-box',
        paddingTop: 'var(--provider-banner-h, 0px)',
        transition: 'padding-top 200ms ease',
      }}
    >
      {/* UnifiedSidebar - always visible on desktop/tablet */}
      {shouldShowNav && <UnifiedSidebar />}

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: 0,
          transition: theme.transitions.create('margin-left', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          ...getMainContentStyles(),
        }}
      >
        {/* MobileHeader for phone */}
        {isMobile && shouldShowNav && <MobileHeader />}

        {/* Page Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      </Box>

    </Box>
  );
};

export default AppShell;
